import { usePosSetupStore } from "@/store/posSetupStore";
import type { UserRecordModel } from '@/utils/PocketBaseAdapter';
import { pb } from '@/utils/PocketBaseAdapter';
import { getFileUrl } from "@/utils/common";

// Shared reactive state
const serverPersonId = ref<string | null>(localStorage.getItem('ServerPerson'));
const serverPersonData = ref<UserRecordModel | undefined>();
const serverPersonAvatar = ref<string>('');
const isLoading = ref<boolean>(false);
const swipeTimeoutMins = ref<number>(0);

// Activity tracking
let inactivityTimer: ReturnType<typeof setTimeout> | null = null;

/**
 * Composable for managing server person state across the application
 */
export function useServerPerson() {
  /**
   * Reset the inactivity timer
   */
  const resetInactivityTimer = () => {
    // Clear existing timer
    if (inactivityTimer) {
      clearTimeout(inactivityTimer);
      inactivityTimer = null;
    }

    // Skip timer if swipeTimeoutMins is 0 or if serverPersonId doesn't exist
    if (swipeTimeoutMins.value === 0 || !serverPersonId.value) {
      return;
    }

    // Set new timer
    inactivityTimer = setTimeout(() => {
      console.log('Inactivity timeout reached, clearing server person');
      clearServerPerson();
    }, swipeTimeoutMins.value * 60 * 1000); // Convert minutes to milliseconds
  };

  /**
   * Set the swipe timeout duration
   */
  const setSwipeTimeout = (minutes: number) => {
    swipeTimeoutMins.value = minutes;
    resetInactivityTimer(); // Reset timer with new duration (or clear if 0)
  };

  /**
   * Track user activity
   */
  const trackActivity = () => {
    // Only reset timer if timeout is enabled (not 0)
    if (swipeTimeoutMins.value > 0) {
      resetInactivityTimer();
    }
  };

  /**
   * Fetch server person details from the database
   */
  const fetchServerPersonDetails = async (id?: string) => {
    const personId = id || serverPersonId.value;
    
    if (!personId) {
      serverPersonData.value = undefined;
      serverPersonAvatar.value = '';
      return;
    }

    try {
      isLoading.value = true;
      const response = await pb.collection('users').getOne<UserRecordModel>(personId);

      if (response) {
        serverPersonData.value = response;
        
        if (response?.avatar) {
          serverPersonAvatar.value = getFileUrl(response, response.avatar, {
            requestKey: 'userprofile',
          });
        } else {
          serverPersonAvatar.value = '';
        }
      }
    } catch (error) {
      console.error('Error fetching server person details:', error);
      // If fetch fails, clear the data
      serverPersonData.value = undefined;
      serverPersonAvatar.value = '';
    } finally {
      isLoading.value = false;
    }
  };

  /**
   * Set the server person and update localStorage
   */
  const setServerPerson = async (userId: string) => {
    serverPersonId.value = userId;
    localStorage.setItem('ServerPerson', userId);
    await fetchServerPersonDetails(userId);
    
    // Start inactivity timer (if enabled)
    resetInactivityTimer();
    
    // Trigger storage event for other tabs/windows
    window.dispatchEvent(new StorageEvent('storage', {
      key: 'ServerPerson',
      newValue: userId,
      oldValue: null,
      storageArea: localStorage,
    }));
  };

  /**
   * Clear the server person
   */
  const clearServerPerson = () => {
    // Clear the inactivity timer
    if (inactivityTimer) {
      clearTimeout(inactivityTimer);
      inactivityTimer = null;
    }

    serverPersonId.value = null;
    serverPersonData.value = undefined;
    serverPersonAvatar.value = '';
    localStorage.removeItem('ServerPerson');
    
    // Trigger storage event for other tabs/windows
    window.dispatchEvent(new StorageEvent('storage', {
      key: 'ServerPerson',
      newValue: null,
      oldValue: serverPersonId.value,
      storageArea: localStorage,
    }));
  };

  /**
   * Sync from localStorage (called on storage events)
   */
  const syncFromStorage = async () => {
    const storedId = localStorage.getItem('ServerPerson');
    
    if (storedId !== serverPersonId.value) {
      serverPersonId.value = storedId;
      
      if (storedId) {
        await fetchServerPersonDetails(storedId);
        resetInactivityTimer(); // Reset timer when syncing (if enabled)
      } else {
        serverPersonData.value = undefined;
        serverPersonAvatar.value = '';
        if (inactivityTimer) {
          clearTimeout(inactivityTimer);
          inactivityTimer = null;
        }
      }
    }
  };

  // Listen for storage events from other tabs/windows
  const handleStorageEvent = (e: StorageEvent) => {
    if (e.key === 'ServerPerson') {
      void syncFromStorage();
    }
  };

  // Set up storage event listener
  if (typeof window !== 'undefined') {
    window.addEventListener('storage', handleStorageEvent);
    
    // Track user activity to reset timer (only if timeout is enabled)
    const activityEvents = ['mousedown', 'keydown', 'touchstart', 'scroll'];
    activityEvents.forEach(event => {
      window.addEventListener(event, trackActivity, { passive: true });
    });
  }

  // Watch for changes to serverPersonId and sync to localStorage
  watch(serverPersonId, (newId) => {
    if (newId) {
      localStorage.setItem('ServerPerson', newId);
      resetInactivityTimer();
    } else {
      localStorage.removeItem('ServerPerson');
      if (inactivityTimer) {
        clearTimeout(inactivityTimer);
        inactivityTimer = null;
      }
    }
  });

  onMounted(async() => {
    const posSetupStore = usePosSetupStore();
    const posSetup = await posSetupStore.load();
    swipeTimeoutMins.value = posSetup?.swipeTimeoutMins || 0;

    // Initialize timer if serverPerson exists on load (and timeout is enabled)
    if (serverPersonId.value) {
      resetInactivityTimer();
    }
  });

  return {
    // State
    serverPersonId,
    serverPersonData,
    serverPersonAvatar,
    isLoading,
    
    // Methods
    fetchServerPersonDetails,
    setServerPerson,
    clearServerPerson,
    syncFromStorage,
    setSwipeTimeout,
    trackActivity,
    resetInactivityTimer,
  };
}
